import { setPreferredRooms } from "#webapp/src/mutations";
import { updatePreferredRoomsMillis } from "#webapp/src/state/actions/settings.actions";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { TextField } from "@clockwise/design-system";
import { Search } from "@clockwise/design-system/icons";
import { Loader } from "@clockwise/web-commons/src/components/loader";
import React from "react";
import toast from "react-hot-toast";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
import { RoomPreferencesCheckbox } from "./RoomPreferencesCheckbox";
import { fragments, query } from "./RoomPreferencesSettings.gql";
import { AugmentedRoom, IContainer, IProps, IState } from "./RoomPreferencesSettingsTypes";

//////////////////
// COMPONENT
//////////////////
class RoomPreferencesSettingsCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      query: "",
      lastProcessedQuery: "",
      loading: false,
      isDirty: false,
      rooms: this.generateRoomResults(props, ""),
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const newState: Partial<IState> = {};
    if (this.state.query !== this.state.lastProcessedQuery) {
      newState.rooms = this.generateRoomResults(nextProps, this.state.query);
      newState.lastProcessedQuery = this.state.query;
    }
    this.setState(newState as IState);
  }

  public componentWillUnmount() {
    if (this.state.isDirty) {
      this.props.updatePreferredRoomsMillis();
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private generateRoomResults = (props: IProps, searchQuery: string) => {
    const roomResults: AugmentedRoom[] = [];
    const suggestedRooms =
      props.org.suggestedRoomPreferences.__typename === "SuggestedRoomPreferences"
        ? props.org.suggestedRoomPreferences.suggestedRooms
        : [];

    // if there is a search query inputted, iterate through those and set the roomResults
    // else just use suggestedRooms
    if (searchQuery && props.viewer.roomQueryResult.__typename === "ExternalRoomSearchResult") {
      props.viewer.roomQueryResult.rooms.forEach((room) => {
        const suggestedRoom = suggestedRooms.find((sr) => sr.room.roomId === room.roomId);
        roomResults.push({
          ...room,
          isSuggested: !!suggestedRoom && suggestedRoom.isSuggested,
          isPreferred: !!suggestedRoom && suggestedRoom.isPreferred,
        });
      });
    } else if (!searchQuery) {
      suggestedRooms.forEach((suggestedRoom) => {
        const { room, isSuggested, isPreferred } = suggestedRoom;
        roomResults.push({ ...room, isSuggested, isPreferred });
      });
    }

    return roomResults.sort((roomA, roomB) => {
      if (roomA.isPreferred && roomA.isSuggested) {
        return -1;
      } else if (roomB.isPreferred && roomB.isSuggested) {
        return 1;
      } else if (roomA.isPreferred) {
        return -1;
      } else if (roomB.isPreferred) {
        return 1;
      } else if (roomA.isSuggested) {
        return -1;
      } else if (roomB.isSuggested) {
        return 1;
      }
      return roomA.name.localeCompare(roomB.name);
    });
  };

  private onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    this.props.relay.refetch({
      queryInput: value,
      excludeCalendars: [],
      orgRelayId: this.props.org.id,
    });
    this.setState({ query: value });
  };

  private onSelectRoom = (roomId: string, isPreferred: boolean) => {
    const suggestedRooms =
      this.props.org.suggestedRoomPreferences.__typename === "SuggestedRoomPreferences"
        ? this.props.org.suggestedRoomPreferences.suggestedRooms
        : [];

    let preferredRoomIds = suggestedRooms
      .filter((sr) => sr.isPreferred)
      .map((pr) => pr.room.roomId);

    if (isPreferred) {
      preferredRoomIds = [...preferredRoomIds, roomId];
    } else {
      const idx = preferredRoomIds.findIndex((preferredRoomId) => preferredRoomId === roomId);
      preferredRoomIds.splice(idx, 1);
    }

    setPreferredRooms(
      this.props.relay.environment,
      { preferredRoomIds, orgRelayId: this.props.org.id },
      () => toast.success("Updated your preferred rooms!"),
      () => toast.error("Failed to update your preferred rooms!"),
    );

    track(TrackingEvents.SETTINGS.UPDATE_PREFERRED_ROOMS, {
      preferredRoomIds: preferredRoomIds.join(", "),
    });

    this.setState({ isDirty: true });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderRoom = (room: AugmentedRoom) => {
    return <RoomPreferencesCheckbox key={room.roomId} room={room} onCheck={this.onSelectRoom} />;
  };

  private renderContent = () => {
    const suggestedRooms =
      this.props.org.suggestedRoomPreferences.__typename === "SuggestedRoomPreferences"
        ? this.props.org.suggestedRoomPreferences.suggestedRooms
        : [];

    if (this.state.loading) {
      return <Loader contained />;
    }

    return (
      <div className="cw-flex cw-flex-col cw-gap-2 cw-mt-1">
        <div className="cw-flex cw-justify-between cw-items-baseline">
          <div className="cw-w-[300px]">
            <TextField
              placeholder="Search for rooms"
              startIcon={Search}
              value={this.state.query}
              onChange={this.onSearch}
              fullWidth
            />
          </div>
          <span className="cw-text-12 cw-text-slate-standard cw-pr-5">
            {`${suggestedRooms.filter((sr) => sr.isPreferred).length} selected`}
          </span>
        </div>
        <div>
          {this.state.rooms.map(this.renderRoom)}
          {this.state.rooms.length < 1 ? <div>No rooms</div> : ""}
        </div>
      </div>
    );
  };
  public render() {
    return <div>{this.renderContent()}</div>;
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  updatePreferredRoomsMillis,
})(RoomPreferencesSettingsCmpt);

//////////////////
// RELAY
//////////////////
export const RoomPreferencesSettings = createRefetchContainer<IContainer>(
  reduxContainer,
  fragments,
  query,
);
